// src/pages/admin/AdminRewardRules.js — Règles de récompense post-achat (v1.65.0)
// "Si le client paie >= X DH -> il gagne un code promo de Y, valable Z jours."
// Maxula
import React, { useEffect, useState, useCallback } from 'react';
import { FiGift, FiPlus, FiTrash2, FiRefreshCw, FiPower, FiEdit2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { rewardAPI } from '../../services/api';

// ---- Aperçu de la règle ----
function RuleCard({ rule }) {
  const color = rule.color || '#0ea5e9';
  const reward = rule.rewardType === 'percentage' ? `${rule.rewardValue || 0}%` : `${rule.rewardValue || 0} DH`;
  return (
    <div className="relative overflow-hidden rounded-2xl text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
      <div className="p-5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider opacity-90">Récompense fidélité</span>
          <FiGift />
        </div>
        <div className="mt-3 text-sm opacity-90">Dès <b>{rule.minSpend || 0} DH</b> d'achat payé</div>
        <div className="mt-1 flex items-end gap-2">
          <span className="text-3xl font-extrabold leading-none">+{reward}</span>
          <span className="opacity-90 text-sm mb-1">offerts en code promo</span>
        </div>
        {rule.label ? <p className="mt-1 text-sm opacity-90">{rule.label}</p> : null}
        <div className="mt-3 flex items-center justify-between text-[11px] opacity-90">
          <span>Valable {rule.validityDays || 30} j</span>
          <span>{Number(rule.minPurchaseNext) > 0 ? `Utilisable dès ${rule.minPurchaseNext} DH` : 'Sans minimum'}</span>
        </div>
      </div>
    </div>
  );
}

const EMPTY = {
  label: '', minSpend: '', rewardType: 'fixed', rewardValue: '', maxReward: '',
  validityDays: 30, minPurchaseNext: '', codePrefix: 'RW', color: '#0ea5e9'
};

export default function AdminRewardRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await rewardAPI.list(); setRules(Array.isArray(data?.data) ? data.data : []); }
    catch (e) { setRules([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.minSpend || Number(form.minSpend) <= 0) { toast.error("Indiquez le seuil de dépense (DH)"); return; }
    if (!form.rewardValue || Number(form.rewardValue) <= 0) { toast.error('Indiquez la valeur de la récompense'); return; }
    if (form.rewardType === 'percentage' && Number(form.rewardValue) > 100) { toast.error('Le pourcentage ne peut dépasser 100'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await rewardAPI.update(editingId, form);
        toast.success('Règle mise à jour ✅');
      } else {
        await rewardAPI.create(form);
        toast.success('Règle de récompense créée ✅');
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      label: r.label ?? '',
      minSpend: r.minSpend ?? '',
      rewardType: r.rewardType ?? 'fixed',
      rewardValue: r.rewardValue ?? '',
      maxReward: r.maxReward ?? '',
      validityDays: r.validityDays ?? 30,
      minPurchaseNext: r.minPurchaseNext ?? '',
      codePrefix: r.codePrefix ?? 'RW',
      color: r.color ?? '#0ea5e9'
    });
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const toggle = async (r) => {
    try { await rewardAPI.update(r.id, { isActive: !r.isActive }); setRules((prev) => prev.map((x) => x.id === r.id ? { ...x, isActive: !x.isActive } : x)); }
    catch (e) { toast.error('Erreur'); }
  };
  const remove = async (r) => {
    if (!window.confirm('Supprimer cette règle ?')) return;
    try { await rewardAPI.remove(r.id); setRules((prev) => prev.filter((x) => x.id !== r.id)); toast.success('Supprimée'); }
    catch (e) { toast.error('Erreur'); }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center gap-2 mb-1">
        <FiGift className="text-[#0ea5e9]" size={22} />
        <h1 className="text-xl md:text-2xl font-bold">Récompenses (cashback)</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">Le client gagne automatiquement un code promo après un achat payé qui atteint le seuil. Le code lui est envoyé et n'est utilisable que par lui, sur un prochain achat.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="font-semibold mb-4">{editingId ? 'Modifier la règle' : 'Nouvelle règle'}</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Libellé (optionnel)</label>
              <input value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="Ex : Cashback 250+" className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Seuil de dépense (DH)</label>
              <input type="number" min="1" value={form.minSpend} onChange={(e) => set('minSpend', e.target.value)} placeholder="Ex : 250" className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Type de récompense</label>
              <select value={form.rewardType} onChange={(e) => set('rewardType', e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="fixed">Montant fixe (DH)</option>
                <option value="percentage">Pourcentage (%)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Valeur récompense {form.rewardType === 'percentage' ? '(%)' : '(DH)'}</label>
              <input type="number" min="1" value={form.rewardValue} onChange={(e) => set('rewardValue', e.target.value)} placeholder={form.rewardType === 'percentage' ? 'Ex : 10' : 'Ex : 40'} className="w-full border rounded-lg px-3 py-2" />
            </div>
            {form.rewardType === 'percentage' && (
              <div>
                <label className="block text-sm font-medium mb-1">Plafond récompense (DH, optionnel)</label>
                <input type="number" min="0" value={form.maxReward} onChange={(e) => set('maxReward', e.target.value)} placeholder="—" className="w-full border rounded-lg px-3 py-2" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Validité du code (jours)</label>
              <input type="number" min="1" value={form.validityDays} onChange={(e) => set('validityDays', e.target.value)} placeholder="30" className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Achat min. pour utiliser (DH, optionnel)</label>
              <input type="number" min="0" value={form.minPurchaseNext} onChange={(e) => set('minPurchaseNext', e.target.value)} placeholder="0" className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Préfixe du code</label>
              <input value={form.codePrefix} onChange={(e) => set('codePrefix', e.target.value)} placeholder="RW" maxLength={6} className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Couleur</label>
              <input type="color" value={form.color} onChange={(e) => set('color', e.target.value)} className="w-full border rounded-lg h-[42px] px-1" />
            </div>
          </div>
          <div className="mt-5 flex items-center gap-2">
            <button onClick={create} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0ea5e9] text-white font-semibold hover:opacity-90 disabled:opacity-60">
              <FiPlus /> {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer les modifications' : 'Créer la règle')}
            </button>
            {editingId && (
              <button onClick={cancelEdit} disabled={saving} className="px-4 py-2.5 rounded-lg border text-gray-600 hover:bg-gray-50">Annuler</button>
            )}
          </div>
        </div>

        {/* Aperçu */}
        <div>
          <h2 className="font-semibold mb-4">Aperçu</h2>
          <RuleCard rule={{ ...form, minSpend: form.minSpend || 0, rewardValue: form.rewardValue || 0 }} />
          <p className="text-xs text-gray-400 mt-3">Exemple : si un client paie {form.minSpend || 250} DH, il gagne {form.rewardType === 'percentage' ? `${form.rewardValue || 10}%` : `${form.rewardValue || 40} DH`} en code promo personnel, valable {form.validityDays || 30} jours.</p>
        </div>
      </div>

      {/* Liste */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Règles existantes ({rules.length})</h2>
          <button onClick={load} className="p-2 border rounded-lg hover:bg-gray-50"><FiRefreshCw size={16} /></button>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0ea5e9]" /></div>
        ) : rules.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-500">Aucune règle de récompense pour l'instant.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {rules.map((r) => (
              <div key={r.id} className="space-y-2">
                <RuleCard rule={r} />
                <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                  <span>{r.scope === 'merchant' ? 'Ma boutique' : 'Toutes boutiques'}</span>
                  <span className={`px-2 py-0.5 rounded-full font-medium ${r.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{r.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => toggle(r)} className="flex-1 px-2 py-1.5 border rounded-lg text-xs hover:bg-gray-50 inline-flex items-center justify-center gap-1"><FiPower size={13} /> {r.isActive ? 'Désactiver' : 'Activer'}</button>
                  <button onClick={() => startEdit(r)} className="px-2 py-1.5 border rounded-lg text-xs text-[#0ea5e9] hover:bg-sky-50 inline-flex items-center justify-center gap-1"><FiEdit2 size={13} /> Modifier</button>
                  <button onClick={() => remove(r)} className="px-2 py-1.5 border rounded-lg text-xs text-red-600 hover:bg-red-50"><FiTrash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-center text-xs text-gray-400 mt-8">Maxula — contact@maxula.ma</p>
    </div>
  );
}
